"use client";

import { useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import ModalManager from "@components/molecules/ModalManager";
import VariantOptionCard from "@components/molecules/ShortlistCard/VariantOption/VariantOptionCard";
import { shortlistCardData } from "@components/molecules/ShortlistCard/ShortlistCard.data";
import assetLoader from "@utils/internalImageLoder";
import { getDeliveryDateFromDays } from "@utils/formatDate";
import styles from "./AppMMVCVariantSwitcher.module.scss";
import modalStyles from "@components/molecules/ShortlistCard/ShortlistCardModals.module.scss";
import { trackAppFlowAction, trackAppFlowView } from "@utils/appFlowEvents";
import { useGlobalLoader } from "@hooks/useGlobalLoader";

const { iconPaths } = shortlistCardData;

const isVariantSellable = (variant: any) => {
  const rawStatus = variant?.status ?? variant?.sellableStatus ?? variant?.sellable_status;
  const status = String(rawStatus ?? "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");

  // Some MMVC responses omit variant status. In that case only a real-time
  // sellable price confirms availability. The on-road reference price and
  // delivery days also exist for unavailable variants and must not make the
  // card selectable.
  if (!status) {
    return Boolean(
      Number(variant?.adspPrice) > 0 || Number(variant?.marketPrice) > 0
    );
  }

  return ["sellable", "on_sale", "available"].includes(status);
};

export default function AppMMVCVariantSwitcher({
  variants = [],
  brandName = "",
  modelName = "",
  modelSlug = "",
}: {
  variants?: any[];
  brandName?: string;
  modelName?: string;
  modelSlug?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader } = useGlobalLoader();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFuel, setSelectedFuel] = useState<string | null>(null);
  const [selectedTransmission, setSelectedTransmission] = useState<string | null>(null);

  const fuelTypes = useMemo(
    () => [...new Set(variants.map((variant) => variant.fuelType).filter(Boolean))],
    [variants]
  );
  const transmissionTypes = useMemo(
    () => [...new Set(variants.map((variant) => variant.transmission).filter(Boolean))],
    [variants]
  );
  const currentSlug = pathname?.split("/").filter(Boolean).pop();
  const currentVariant = variants.find((variant) => variant.slug === currentSlug);
  const filteredVariants = useMemo(
    () =>
      variants
        .filter((variant) =>
          (!selectedFuel || variant.fuelType === selectedFuel) &&
          (!selectedTransmission || variant.transmission === selectedTransmission)
        )
        .sort((first, second) => {
          if (first.slug === currentSlug) return -1;
          if (second.slug === currentSlug) return 1;
          return Number(!isVariantSellable(first)) - Number(!isVariantSellable(second));
        }),
    [variants, selectedFuel, selectedTransmission, currentSlug]
  );

  const navigateToVariant = (slug: string) => {
    showLoader();
    setIsOpen(false);
    const selectedVariant = variants.find((variant) => variant.slug === slug);
    void trackAppFlowAction(
      {
        pageType: "MMV page",
        brandName,
        modelName,
        variantName: selectedVariant?.variantName,
      },
      {
        action_type: "tap_btn",
        action_name: "tap_btn_variant_selected",
        vehicle_variant: selectedVariant?.variantName || "",
      },
    );
    router.push(`/acko-app/cars/${slug}/`);
  };

  const handleCompareVariants = () => {
    showLoader();
    setIsOpen(false);
    void trackAppFlowAction(
      {
        pageType: "MMV page",
        brandName,
        modelName,
      },
      {
        action_type: "tap_btn",
        action_name: "tap_btn_add_to_compare",
        section: "Compare",
      },
    );
    router.push(`/acko-app/compare/${modelSlug || currentSlug || ""}`);
  };

  return (
    <>
      <button
        type="button"
        className={styles.editButton}
        onClick={() => {
          setIsOpen(true);
          void trackAppFlowAction(
            {
              pageType: "MMV page",
              brandName,
              modelName,
              variantName: currentVariant?.variantName,
            },
            {
              action_type: "tap_btn",
              action_name: "tap_btn_choose_a_variant",
            },
          );
          void trackAppFlowView(
            {
              pageType: "MMV page",
              brandName,
              modelName,
            },
            {
              view_type: "view_modal",
              view_name: "view_modal_variant_selection",
            },
          );
        }}
        aria-label="Edit variant"
      >
        <Image
          src={`${assetLoader()}/icons/Edit_pencil.png`}
          alt=""
          width={24}
          height={24}
        />
      </button>
      <ModalManager
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Select a variant"
        customClass={modalStyles.variantModalContent}
        modalHeaderClass={modalStyles.variantModalHeader}
        modalBodyClass={modalStyles.variantModalBody}
      >
        <button
          type="button"
          className={modalStyles.compareBlock}
          onClick={handleCompareVariants}
        >
          <div className={modalStyles.compareIcon}>
            <Image
              src={`${assetLoader()}${iconPaths.compareCars}`}
              alt=""
              width={24}
              height={24}
            />
          </div>
          <div className={modalStyles.compareText}>
            <span className={modalStyles.compareLabel}>
              Not sure which variant to pick?
            </span>
            <span className={modalStyles.compareCta}>
              Compare variants
              <Image
                src={`${assetLoader()}${iconPaths.chevronRightBlue}`}
                alt=""
                width={16}
                height={16}
              />
            </span>
          </div>
        </button>
        <div className={modalStyles.filterRow}>
          {!!fuelTypes.length && (
            <div className={modalStyles.filterGroup}>
              {fuelTypes.map((fuelType) => (
                <button
                  key={fuelType}
                  type="button"
                  className={`${modalStyles.filterChip} ${selectedFuel === fuelType ? modalStyles.filterChipActive : ""}`}
                  onClick={() => setSelectedFuel((value) => value === fuelType ? null : fuelType)}
                >
                  {fuelType}
                </button>
              ))}
            </div>
          )}
          {!!fuelTypes.length && !!transmissionTypes.length && <span className={modalStyles.filterDivider} />}
          {!!transmissionTypes.length && (
            <div className={modalStyles.filterGroup}>
              {transmissionTypes.map((transmission) => (
                <button
                  key={transmission}
                  type="button"
                  className={`${modalStyles.filterChip} ${selectedTransmission === transmission ? modalStyles.filterChipActive : ""}`}
                  onClick={() => setSelectedTransmission((value) => value === transmission ? null : transmission)}
                >
                  {transmission}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={modalStyles.variantList}>
          {filteredVariants.map((variant) => (
            <VariantOptionCard
              key={variant.slug}
              variantName={variant.variantName}
              fuelType={variant.fuelType}
              transmission={variant.transmission}
              mileage={variant.mileage}
              isSellable={isVariantSellable(variant)}
              adspPrice={variant.adspPrice}
              marketPrice={variant.marketPrice}
              savings={variant.ackoDriveSavings}
              fallbackPrice={variant.onRoadPrice || variant.marketPrice}
              isExpressDelivery={Boolean(variant.expressDelivery)}
              deliveryDate={getDeliveryDateFromDays(
                variant.expressDelivery
                  ? variant.expressDeliveryDays
                  : variant.standardDeliveryDays
              )}
              isSelected={variant.slug === currentSlug}
              onClick={() => isVariantSellable(variant) && navigateToVariant(variant.slug)}
            />
          ))}
          {!filteredVariants.length && <p className={styles.emptyState}>No variants match your filter.</p>}
        </div>
      </ModalManager>
    </>
  );
}
